import { uniq } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useErrorToast } from '~/common/errors/useErrorToast';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { testHasTextMatch } from '~/eds/src/utils/testHasTextMatch';
import { EntityType } from '~/enums/EntityType';
import { useEsignatureActivityLogs } from '~/features/ticket/activity-log/useEsignatureActivityLogs';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import { PilotId } from '~/types/PilotId';
import { TicketDocumentVersion } from '~/types/Ticket';
import { Uuid } from '~/types/Uuid';
import { tokenizeContent } from '~/utils/comments';

import { ActivityLogItem } from './ActivityLogItem';
import { CustomActions } from './types';
import {
  getActivityLogContent,
  getQuote,
  tokenRenderer,
  tokenSearchRenderer,
} from './utils';

interface Props {
  versions: TicketDocumentVersion[];
  ticketId: Uuid;
  searchInput: string;
  customActions?: CustomActions;
}

export function ActivityLog({
  customActions,
  searchInput,
  ticketId,
  versions = [],
}: Props) {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const { toast } = useErrorToast();

  const { data: activities = [], isLoading: isLoadingActivities } =
    api.endpoints.getActivities.useQuery({
      entityId: ticketId,
      entityType: EntityType.Ticket,
    });

  const currentUser = useCurrentUser();

  const [resolveUsers, { data: usersData = {}, isLoading: isLoadingUsers }] =
    api.endpoints.resolveUsers.useLazyQuery();

  const { items: esignatureActivities, error: esignatureActivityError } =
    useEsignatureActivityLogs({
      ticketId,
    });

  useEffect(() => {
    if (activities.length && currentUser?.client) {
      const userIds: number[] = uniq(
        activities
          .map((event) =>
            tokenizeContent(
              getActivityLogContent(
                event,
                versions,
                ticketId,
                intl,
                formatDate,
              ),
              {
                user: (userId: PilotId) => Number(userId),
              },
            )
              .filter((value) => value.type === 'user')
              .map((value) => value.content),
          )
          .flat(),
      );
      resolveUsers({
        ids: userIds,
        params: { clientId: currentUser.client, includeDeleted: true },
      });
    }
  }, [
    activities,
    currentUser?.client,
    formatDate,
    intl,
    resolveUsers,
    versions,
    ticketId,
  ]);

  const ticketActivities = useMemo(() => {
    if (isLoadingUsers) {
      return [];
    }

    return activities.map((event) => {
      const componentsRenderer = tokenRenderer(
        usersData,
        customActions,
        versions,
        intl,
      );

      const content = getActivityLogContent(
        event,
        versions,
        ticketId,
        intl,
        formatDate,
      );
      const quote = getQuote(event);
      const contentTokens = tokenizeContent(content, componentsRenderer);

      const searchTextRenderer = tokenSearchRenderer(usersData, intl);
      const contentSearchText = tokenizeContent(content, searchTextRenderer)
        .map((token) => token.content)
        .join('');

      return {
        id: event.id,
        quote: quote
          ? tokenizeContent(quote, componentsRenderer).map((token, i) => (
              <React.Fragment key={i}>{token.content}</React.Fragment>
            ))
          : undefined,
        content: contentTokens.map((token, i) => (
          <React.Fragment key={i}>{token.content}</React.Fragment>
        )),
        contentSearchText,
        quoteSearchText: quote
          ? tokenizeContent(quote, searchTextRenderer)
              .map((token) => token.content)
              .join('')
          : undefined,
        dateTime: event.modifiedDate,
      };
    });
  }, [
    isLoadingUsers,
    activities,
    usersData,
    customActions,
    versions,
    intl,
    ticketId,
    formatDate,
  ]);

  const activityLogs = useMemo(() => {
    if (esignatureActivities.length === 0) {
      return ticketActivities;
    }

    return [...ticketActivities, ...esignatureActivities].sort(
      (a, b) => (b.dateTime?.getTime() ?? 0) - (a.dateTime?.getTime() ?? 0),
    );
  }, [ticketActivities, esignatureActivities]);

  const activityLogsFiltered = useMemo(() => {
    if (!searchInput) return activityLogs;
    return activityLogs.filter(
      (a) =>
        (a.contentSearchText &&
          testHasTextMatch(a.contentSearchText, searchInput)) ||
        (a.quoteSearchText && testHasTextMatch(a.quoteSearchText, searchInput)),
    );
  }, [searchInput, activityLogs]);

  useEffect(() => {
    if (esignatureActivityError && esignatureActivityError instanceof Error) {
      toast(esignatureActivityError, { autoDismiss: false });
    }
  }, [esignatureActivityError]);

  return (
    <ContentContainer
      loadingContent={{
        isLoading: isLoadingActivities || isLoadingUsers,
        message: intl.formatMessage({
          id: 'ActivityLog.loadingMessage',
          defaultMessage: 'Activity Log is being loaded.',
          description: 'Message shown when activity log is loading',
        }),
      }}
      placeholderContent={
        activityLogsFiltered.length
          ? undefined
          : {
              icon: 'alert',
              message: intl.formatMessage({
                id: 'ActivityLog.noActivitiesMessage',
                defaultMessage: 'There are no activities to be displayed.',
                description: 'Message shown when no activities are available',
              }),
            }
      }
    >
      <Layout spacing={2} direction="column" role="list">
        {activityLogsFiltered.map((log) => (
          <ActivityLogItem
            key={log.id}
            content={log.content}
            dateTime={log.dateTime}
            quote={log.quote}
          />
        ))}
      </Layout>
    </ContentContainer>
  );
}
